<template>
  <InputCombobox
    v-model="modelValue"
    :items="items"
    items-key-name="id"
    v-bind="inheritedAttrs"
    @input="scheduleSearchKVK"
    @query-changed="query = $event"
    :is-loading="isSearchingKVK"
    search-thoroughly
    new-item-text="Een ander bedrijf toevoegen"
    ref="combobox"
    class="known-business-entity-combobox"
  >
    <template v-if="$slots['before-input']" #before-input>
      <slot name="before-input" />
    </template>
    <template #custom-options="{ disabled, onMouseDownLeft, onMouseUpLeft }">
      <div v-if="wrappedKVKResults.length > 0" class="gray-lighten-2 p-2 font-medium">
        <span>Resultaten uit het handelsregister</span>
      </div>

      <CustomComboboxOption
        v-for="wrappedKVKResult in wrappedKVKResults"
        :key="wrappedKVKResult.item.id"
        :wrapper="wrappedKVKResult"
        @option-mousedown.left="onMouseDownLeft"
        @option-mouseup.left="onMouseUpLeft"
        :disabled="disabled"
      >
        <template #text="{ active, selected }">
          <div class="min-w-0">
            <span :class="['block truncate', selected && 'font-semibold']">
              {{ wrappedKVKResult.item.naam }}
            </span>
            <div class="truncate text-sm" :class="active ? 'text-gray-lighten-2/50' : ''">
              {{ wrappedKVKResult.item.kvkNummer }} -
              {{ wrappedKVKResult.item.adres?.binnenlandsAdres?.straatnaam }},
              {{ wrappedKVKResult.item.adres?.binnenlandsAdres?.plaats }}
            </div>
          </div>
        </template>
        <template #after-text>
          <PBadge color="primary-lighten-1" class="text-nowrap">
            <FontAwesomeIcon :icon="['fas', 'plus']" size="xs" />
            <span>Bedrijf aanmaken</span>
          </PBadge>
        </template>
      </CustomComboboxOption>

      <div v-if="totalKVKResultsCount > 5" class="gray-lighten-2 px-2 py-1.5 text-xs">
        Eerste 5 van {{ totalKVKResultsCount }} resultaten. Verfijn je zoekopdracht om een specifiek
        bedrijf te vinden.
      </div>
    </template>
    <template #loading-text>Meer bedrijven zoeken...</template>
  </InputCombobox>
</template>

<script setup lang="ts">
// Do not inherit all attrs because v-model will be inherited by InputCombobox, which we don't want
import { computed, ref, useAttrs, useTemplateRef } from "vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import PBadge from "@/components/elements/PBadge.vue"
import CustomComboboxOption from "@/components/inputs/input_combobox/CustomComboboxOption.vue"
import { KVKRepository } from "@/repositories/external/kvk-repository.ts"
import { CustomComboboxOptionWrapper } from "@/components/inputs/input_combobox/CustomComboboxOptionWrapper.ts"
import { useModelFormStore } from "@/store/model-forms.store.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { ModelFactory } from "@/factories/model-factory.ts"

withDefaults(
  defineProps<{
    items: KnownBusinessEntity[]
  }>(),
  {}
)

defineOptions({
  inheritAttrs: false
})
const attrs = useAttrs()

const inheritedAttrs = computed((): Record<string, unknown> => {
  const _inheritedAttrs: Record<string, unknown> = { ...attrs }
  delete _inheritedAttrs["v-model"] // We want all attrs to be inherited, except v-model
  delete _inheritedAttrs["v-on:input"] // We want all attrs to be inherited, except v-model
  return _inheritedAttrs
})

const modelValue = defineModel<KnownBusinessEntity | null>()

const query = ref("")
const KVKSearchResults = ref<any[]>([])
const totalKVKResultsCount = ref(0)
const isSearchingKVK = ref(false)
const combobox = useTemplateRef<typeof InputCombobox>("combobox")

function scheduleSearchKVK() {
  isSearchingKVK.value = true
  const _query = query.value
  delayedSearchKVK.schedule(_query)
}
const delayedSearchKVK = useDelayedFunction(searchKVK, 200)

async function searchKVK(searchTerm: string) {
  const maxResults = 5

  if (!searchTerm || searchTerm.trim().length === 0) {
    KVKSearchResults.value = []
    totalKVKResultsCount.value = 0
    isSearchingKVK.value = false
    return
  }

  const result = await new KVKRepository().searchByName(searchTerm)
  isSearchingKVK.value = false
  if (result.resultaten) {
    const withAddress = result.resultaten.filter((item: any) => item.adres)
    totalKVKResultsCount.value = withAddress.length
    let count = 0
    KVKSearchResults.value = withAddress
      .slice(0, maxResults) // Limit to maxResults
      .map((item: any) => ({
        // Add an id property for reactivity keying
        ...item,
        id: count++
      }))
  } else {
    KVKSearchResults.value = []
    totalKVKResultsCount.value = 0
  }
}

const modelFormStore = useModelFormStore()
const wrappedKVKResults = computed<CustomComboboxOptionWrapper<any>[]>(() => {
  return KVKSearchResults.value.map((result) => {
    return new CustomComboboxOptionWrapper(result, () => {
      modelFormStore.openForm({
        model: ModelFactory.createModel(KnownBusinessEntity),
        textInput: query.value,
        creationParameters: {
          // Pass the selected KVK result to creationParameters so it is automatically selected in the combobox.
          KVKResult: result
        },
        saveHandle: combobox.value?.selectItem // Auto-select newly created item
      })
    })
  })
})
</script>

<style scoped></style>
